import { CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { ReactNode } from "react";
import { ExtendedTitle, UpdateKeyTitle } from "../../../types/apiTypes";
import {
  CheckIcon,
  CloseIcon,
  DeleteIcon,
  DragFieldIcon,
  StarIcon,
  StarWithBorderIcon,
} from "../../images";
import { ReuseTooltip } from "../../ui";

type ItemProps = {
  item: ExtendedTitle;
  children: ReactNode;
  isIgnored: boolean;
};

const Item = ({ item, children, isIgnored }: ItemProps) => {
  return (
    <Stack flexDirection="row" gap="4px" data-id={item.id}>
      <Stack
        sx={{
          padding: "4px",
          borderRadius: "6px",
          backgroundColor: item.is_approved
            ? isIgnored
              ? "#F2D7D5"
              : "#F4F5F7"
            : "rgba(241, 99, 0, 0.1)",
          flexDirection: "row",
          alignItems: "center",
          flex: 1,
        }}
      >
        <Stack
          sx={{
            cursor: "grab",
            flexDirection: "row",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <DragFieldIcon
            color={
              item.is_approved ? (isIgnored ? "#D98880" : "#353535") : "#FEA05E"
            }
          />

          <Typography
            component="span"
            variant="body1"
            color="custom.textDarkBlue"
            sx={{
              whiteSpace: "nowrap",
              fontWeight: 500,
            }}
          >
            {item.name}
          </Typography>
        </Stack>

        <Stack flexDirection="row" flexWrap="nowrap" paddingLeft="4px">
          {children}
        </Stack>
      </Stack>
    </Stack>
  );
};

const iconStyles = {
  height: "16px",
  width: "16px",
};

const iconButtonStyles = {
  height: "24px",
  width: "24px",
  padding: "0px",
};

type Props = {
  approvedLength: number;
  ignoredLength: number;
  item: ExtendedTitle;
  changeHandler: (data: Omit<UpdateKeyTitle, "campaign_id">) => void;
  removeHandler: (id: string) => void;
  loading?: boolean;
  isIgnored?: boolean;
};

export const KeyTitlesItem = ({
  approvedLength,
  ignoredLength,
  item,
  changeHandler,
  removeHandler,
  loading,
  isIgnored = false,
}: Props) => {
  return (
    <Item item={item} isIgnored={isIgnored}>
      {item.is_approved ? (
        loading ? (
          <CircularProgress size={16} sx={{ color: "custom.textDarkBlue" }} />
        ) : (
          <>
            {!isIgnored && (
              <ReuseTooltip
                text={
                  item.is_critical
                    ? "Unmark job title as critical"
                    : "Mark job title as critical"
                }
              >
                <IconButton
                  className="no-drag"
                  sx={iconButtonStyles}
                  onClick={() =>
                    changeHandler({
                      id: item.id,
                      is_critical: !item.is_critical,
                      position: !item.is_critical ? 0 : undefined,
                    })
                  }
                >
                  {item.is_critical ? (
                    <StarIcon {...iconStyles} color="#1D3E70" />
                  ) : (
                    <StarWithBorderIcon
                      {...iconStyles}
                      color="rgba(0, 0, 0, 0.3)"
                    />
                  )}
                </IconButton>
              </ReuseTooltip>
            )}

            <ReuseTooltip text="Delete job title">
              <IconButton
                className="no-drag"
                sx={iconButtonStyles}
                onClick={() => removeHandler(item.id)}
              >
                <DeleteIcon
                  {...iconStyles}
                  color={isIgnored ? "#D98880" : "#9B9B9B"}
                />
              </IconButton>
            </ReuseTooltip>
          </>
        )
      ) : loading ? (
        <CircularProgress size={16} sx={{ color: "primary" }} />
      ) : (
        <>
          <ReuseTooltip text="Add job title to ignore list">
            <IconButton
              className="no-drag"
              sx={iconButtonStyles}
              onClick={() =>
                changeHandler({
                  id: item.id,
                  is_ignored: true,
                  is_approved: true,
                  position: ignoredLength,
                })
              }
            >
              <CloseIcon {...iconStyles} />
            </IconButton>
          </ReuseTooltip>

          <ReuseTooltip text="Add job title to approved">
            <IconButton
              className="no-drag"
              sx={iconButtonStyles}
              onClick={() =>
                changeHandler({
                  id: item.id,
                  is_approved: true,
                  position: approvedLength,
                })
              }
            >
              <CheckIcon {...iconStyles} />
            </IconButton>
          </ReuseTooltip>
        </>
      )}
    </Item>
  );
};
